import React from 'react';
import { CheckboxVisibility, DetailsList, DetailsListLayoutMode, IColumn, IGroup, Modal } from '@fluentui/react';
import { pageDataService } from 'common/utilities/PageDataService';

import { IPerformanceTimingData, ISplitTiming } from '../PerformanceService.types';

import './PerformanceDialog.scss';

interface IPerformanceStatisticsDialogProps {
  performanceData: IPerformanceTimingData;
  isOpen: boolean;
  onDismiss?: (ev?: Event | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
}

interface IPerformanceItem {
  key: string;
  name: string;
  start?: number;
  duration?: number;
}

/* eslint-disable react/destructuring-assignment, i18next/no-literal-string */
export class PerformanceDialog extends React.PureComponent<IPerformanceStatisticsDialogProps> {
  private columns: IColumn[];

  constructor(props: IPerformanceStatisticsDialogProps) {
    super(props);

    this.columns = [
      { key: 'name', name: 'Name', fieldName: 'name', minWidth: 300, isResizable: true },
      { key: 'start', name: 'Start', fieldName: 'start', minWidth: 50, onRender: this._renderStart },
      { key: 'duration', name: 'Duration', fieldName: 'duration', minWidth: 70, onRender: this._renderDuration },
      { key: 'props', name: 'Properties', fieldName: 'props', minWidth: 100 },
    ];
  }

  private getClientTimings(): IPerformanceItem[] {
    const { preRequestTime = 0, serverTime = 0, pageLoadTime, navigationStartTime, splitTimings = [] } = this.props.performanceData;
    const clientTime = pageLoadTime - serverTime - preRequestTime;

    let clientOthersDuration = clientTime;
    if (splitTimings.length > 0) {
      clientOthersDuration = splitTimings[0].timestamp - navigationStartTime - preRequestTime - serverTime;
    }

    const clientTimings: IPerformanceItem[] = [
      {
        key: 'domProcessing',
        name: 'DomProcessing',
        start: 0,
        duration: clientOthersDuration,
      },
    ];

    if (splitTimings) {
      splitTimings.forEach((t: ISplitTiming) => {
        const clientStartTime = t.timestamp - navigationStartTime - preRequestTime - serverTime;
        clientTimings.push({
          key: t.name,
          name: t.name,
          start: clientStartTime,
          duration: t.duration,
        });
      });
    }

    return clientTimings;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _renderStart = (item: IPerformanceItem, index?: number, column?: IColumn): JSX.Element => {
    return (
      <span
        style={{
          display: 'block',
          textAlign: 'right',
        }}
      >
        {item.start}
      </span>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _renderDuration = (item: IPerformanceItem, index?: number, column?: IColumn): JSX.Element => {
    return (
      <span
        style={{
          display: 'block',
          textAlign: 'right',
        }}
      >
        {item.duration}
      </span>
    );
  };

  private _renderChart(): JSX.Element | null {
    const { preRequestTime, serverTime, pageLoadTime, requestTime, responseTime } = this.props.performanceData;

    if (!serverTime) {
      return null;
    }

    const preRequestDuration = preRequestTime || 0;
    const requestDuration = requestTime || 0;
    const responseDuration = responseTime || 0;
    const clientTime = pageLoadTime - serverTime - preRequestDuration;
    const chartStartPosition = 120;
    const chartWidth = 400;
    const preRequestWidth = (preRequestDuration / pageLoadTime) * chartWidth;
    const requestWidth = (requestDuration / pageLoadTime) * chartWidth;
    const responseWidth = (responseDuration / pageLoadTime) * chartWidth;
    const clientWidth = (clientTime / pageLoadTime) * chartWidth;
    const clientProcessingX = chartStartPosition + preRequestWidth + requestWidth + responseWidth;

    const clientTimings = this.getClientTimings();
    const measurements = clientTimings.filter((t) => t.duration) || [];
    const svgHeight = 80 + measurements.length * 20;

    const renderMeasurements = () => {
      return measurements.map((m, index) => {
        let measurementStartTime = m.start! - m.duration!;
        if (measurementStartTime < 0) {
          measurementStartTime = 0;
        }
        const x = clientProcessingX + (measurementStartTime / pageLoadTime) * chartWidth;
        const width = (m.duration! / pageLoadTime) * chartWidth;
        let { name } = m;
        if (name.startsWith('End')) {
          name = name.slice(3);
        }

        return (
          <g key={m.name} className="bar">
            <rect x={x} y={80 + 20 * index} width={width} height="19" />
            <text x={x} y={88 + 20 * index} dy=".20em" fontSize="x-small">
              {name} {m.duration}ms
            </text>
          </g>
        );
      });
    };

    return (
      <svg width={chartWidth + 280} height={svgHeight} role="img">
        <g className="bar">
          <text x="0" y="10" dy=".35em">
            Pre-Request
          </text>
          <rect x={chartStartPosition} width={preRequestWidth} height="19" />
          <text x={chartStartPosition} y="10" dy=".35em">
            {preRequestTime}ms
          </text>
        </g>
        <g className="bar">
          <text x="0" y="28" dy=".35em">
            Request
          </text>
          <rect x={chartStartPosition + preRequestWidth} y="20" width={requestWidth} height="19" />
          <text x={chartStartPosition + preRequestWidth} y="28" dy=".35em">
            {requestTime}ms
          </text>
        </g>
        <g className="bar">
          <text x="0" y="48" dy=".35em">
            Response
          </text>
          <rect x={chartStartPosition + preRequestWidth + requestWidth} y="40" width={responseWidth} height="19" />
          <text x={chartStartPosition + preRequestWidth + requestWidth} y="48" dy=".35em">
            {responseDuration}ms
          </text>
        </g>
        <g className="bar">
          <text x="0" y="68" dy=".35em">
            Client processing
          </text>
          <rect x={clientProcessingX} y="60" width={clientWidth} height="19" />
          <text x={clientProcessingX} y="68" dy=".35em">
            {clientTime}ms
          </text>
        </g>
        {renderMeasurements()}
      </svg>
    );
  }

  private _renderDetailsList(): JSX.Element {
    const {
      preRequestTime = 0,
      fetchTime = 0,
      DNSTime = 0,
      TCPTime = 0,
      serverTime = 0,
      requestTime = 0,
      responseTime,
      pageLoadTime,
    } = this.props.performanceData;
    const preFetchDuration = preRequestTime - fetchTime - DNSTime - TCPTime;
    const clientTime = pageLoadTime - serverTime - preRequestTime;
    const isPageRefreshed = !!serverTime;

    let items: IPerformanceItem[] = [
      { key: 'pre-fetch', name: 'Pre-Fetch', start: 0, duration: preFetchDuration },
      { key: 'fetch', name: 'Fetch', start: preFetchDuration, duration: fetchTime },
      { key: 'dns', name: 'DNS', start: preFetchDuration + fetchTime, duration: DNSTime },
      { key: 'tcp', name: 'TCP', start: preFetchDuration + fetchTime + DNSTime, duration: TCPTime },
    ];

    const serverTimings = pageDataService.getServerTimings();
    let serverTotalTime = 0;
    serverTimings.forEach((data) => {
      items.push({ key: data.name, name: data.name, duration: data.durationMilliseconds });
      serverTotalTime += data.durationMilliseconds;
    });
    // "others" entry shows the total server execution time that was not measured
    items.push({ key: 'server-others', name: 'Others', duration: requestTime - serverTotalTime });
    items.push({ key: 'response', name: 'Response', duration: responseTime });

    const clientTimings = this.getClientTimings();
    items = items.concat(clientTimings);

    const groups: IGroup[] = [];
    if (isPageRefreshed) {
      groups.push({
        key: 'pre-request',
        name: `Pre-Request: ${preRequestTime} ms`,
        startIndex: 0,
        count: 4,
        level: 0,
      });
      groups.push({
        key: 'server',
        name: `Server: ${serverTime} ms`,
        startIndex: 4,
        count: serverTimings.length + 2,
        level: 0,
      });
    }
    groups.push({
      key: 'client',
      name: `Client: ${clientTime} ms`,
      startIndex: 6 + serverTimings.length,
      count: clientTimings.length,
      level: 0,
    });

    return (
      <div className="detailsList">
        <DetailsList
          checkboxVisibility={CheckboxVisibility.hidden}
          items={items}
          groups={groups}
          columns={this.columns}
          groupProps={{
            showEmptyGroups: true,
          }}
          layoutMode={DetailsListLayoutMode.justified}
          compact={true}
        />
      </div>
    );
  }

  public render(): JSX.Element {
    return (
      <Modal
        isOpen={this.props.isOpen}
        isBlocking={true}
        onDismiss={this.props.onDismiss}
        overlay={{ styles: { root: { display: 'none' } } }}
      >
        <div className="perf-dialog-content">
          <h3>Performance Statistics</h3>
          {this._renderChart()}
          {this._renderDetailsList()}
        </div>
      </Modal>
    );
  }
}
